/* eslint-disable react/no-array-index-key */
import { FC } from 'react';

import Image from 'molecules/Image';

import FeatureTableRow from 'components/Tables/FeatureComparisonTable/components/FeatureTableRow';
import { FeatureComparisonTableConfigProps } from 'components/Tables/FeatureComparisonTable/config';

import { hasArrayValues } from 'utils/array/arrays';

const DesktopFeatureTable: FC<FeatureComparisonTableConfigProps> = ({
  tableHeading,
  columnOneTitle,
  columnOneHighlighted,
  columnOneIsPrismatic,
  columnTwoTitle,
  columnTwoHighlighted,
  columnTwoIsPrismatic,
  columnThreeTitle,
  columnThreeHighlighted,
  columnThreeIsPrismatic,
  tableSections,
}) => {
  // Determine which column is which based on isPrismatic flags
  const getPrismaticColumn = () => {
    if (columnOneIsPrismatic) {
      return { title: columnOneTitle, highlighted: columnOneHighlighted, key: 'one' as const };
    }
    if (columnTwoIsPrismatic) {
      return { title: columnTwoTitle, highlighted: columnTwoHighlighted, key: 'two' as const };
    }
    if (columnThreeIsPrismatic) {
      return { title: columnThreeTitle, highlighted: columnThreeHighlighted, key: 'three' as const };
    }

    return null;
  };

  const getInHouseColumn = () => {
    if (columnOneTitle.toLowerCase().includes('in-house') || columnOneTitle.toLowerCase().includes('in house')) {
      return { title: columnOneTitle, highlighted: columnOneHighlighted, key: 'one' as const };
    }
    if (columnTwoTitle.toLowerCase().includes('in-house') || columnTwoTitle.toLowerCase().includes('in house')) {
      return { title: columnTwoTitle, highlighted: columnTwoHighlighted, key: 'two' as const };
    }
    if (columnThreeTitle.toLowerCase().includes('in-house') || columnThreeTitle.toLowerCase().includes('in house')) {
      return { title: columnThreeTitle, highlighted: columnThreeHighlighted, key: 'three' as const };
    }

    return null;
  };

  const getTraditionalColumn = () => {
    if (columnOneTitle.toLowerCase().includes('traditional') || columnOneTitle.toLowerCase().includes('ipaas')) {
      return { title: columnOneTitle, highlighted: columnOneHighlighted, key: 'one' as const };
    }
    if (columnTwoTitle.toLowerCase().includes('traditional') || columnTwoTitle.toLowerCase().includes('ipaas')) {
      return { title: columnTwoTitle, highlighted: columnTwoHighlighted, key: 'two' as const };
    }
    if (columnThreeTitle.toLowerCase().includes('traditional') || columnThreeTitle.toLowerCase().includes('ipaas')) {
      return { title: columnThreeTitle, highlighted: columnThreeHighlighted, key: 'three' as const };
    }

    return null;
  };

  const prismaticCol = getPrismaticColumn();
  const inHouseCol = getInHouseColumn();
  const traditionalCol = getTraditionalColumn();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getColumnByKey = (row: any, key?: 'one' | 'two' | 'three') => {
    if (!key) {
      return undefined;
    }
    if (key === 'one') {
      return row.columnOne;
    }
    if (key === 'two') {
      return row.columnTwo;
    }

    return row.columnThree;
  };

  const renderColumnTitle = (title: string, isPrismatic?: boolean) => {
    if (isPrismatic) {
      return (
        <div className="flex items-center justify-start">
          <Image url="/prismatic-logo.svg" alt="Prismatic" className="h-[34px] w-40 object-contain" />
          <span className="sr-only">Prismatic</span>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-start">
        <span className="font-spaceGrotesk text-2xl font-medium leading-8 text-white">{title}</span>
      </div>
    );
  };

  return (
    <div className="relative hidden w-full overflow-visible md:flex md:flex-col">
      <div className="relative rounded-xl border border-primary-300/30 bg-[radial-gradient(ellipse_98.08%_31.01%_at_50.00%_49.88%,_rgba(255,_255,_255,_0.03)_0%,_rgba(255,_255,_255,_0)_100%)] backdrop-blur-xl">
        <div className="flex items-center gap-4 px-1 pb-1 pt-6 md:gap-4 md:px-6">
          <div className="w-[210px] pl-2 md:w-[230px] md:pl-4 xl:w-[274px]">
            <h2 className="font-spaceGrotesk text-[24px] font-medium leading-[32px] text-white md:text-[30px] md:leading-[38px]">
              {tableHeading}
            </h2>
          </div>

          <div className="flex flex-1 items-center justify-start gap-4 md:gap-4 xl:gap-[30px]">
            {/* Prismatic */}
            <div className="flex flex-1 items-center justify-start">
              {prismaticCol && renderColumnTitle(prismaticCol.title, true)}
            </div>
            {/* In-House */}
            <div className="flex flex-1 items-center justify-start">
              {inHouseCol && renderColumnTitle(inHouseCol.title, false)}
            </div>
            {/* Traditional iPaaS */}
            <div className="flex flex-1 items-center justify-start">
              {traditionalCol && renderColumnTitle(traditionalCol.title, false)}
            </div>
          </div>
        </div>

        <div className="px-4 pb-6 pt-2 md:p-6">
          <div className="flex flex-col overflow-hidden rounded-lg border border-primary-300/30">
            {hasArrayValues(tableSections) &&
              tableSections.map(section => (
                <div key={section.id}>
                  {hasArrayValues(section.rows) &&
                    section.rows.map((row, idx) => {
                      // Map columns to correct order using helper
                      const prismaticColumn = getColumnByKey(row, prismaticCol?.key);
                      const inHouseColumn = getColumnByKey(row, inHouseCol?.key);
                      const traditionalColumn = getColumnByKey(row, traditionalCol?.key);

                      return (
                        <FeatureTableRow
                          key={`${section.id}-row-${idx}`}
                          rowHeading={row.rowHeading}
                          prismaticColumn={prismaticColumn}
                          inHouseColumn={inHouseColumn}
                          traditionalColumn={traditionalColumn}
                          prismaticHighlighted={prismaticCol?.highlighted}
                          inHouseHighlighted={inHouseCol?.highlighted}
                          traditionalHighlighted={traditionalCol?.highlighted}
                          columnOne={row.columnOne}
                          columnTwo={row.columnTwo}
                          columnThree={row.columnThree}
                        />
                      );
                    })}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopFeatureTable;
